<script lang="ts">
  import ConfigureConnectionGrid from "$lib/ui/configure-connection-grid/grid.svelte";
  import { ssoProviders } from "$lib/utils/constants";

  import { push } from "svelte-spa-router";
  import OnboardingWizardShell from "$lib/components/onboarding/OnboardingWizardShell.svelte";

  import { selectedOrgStore } from "$stores/orgs";
  import InviteAdminForm from "$lib/components/integrations/InviteAdminFlyoverV2.svelte";
  import SuccessfulFlyover from "$lib/components/integrations/SuccessfullConnectionFlyover.svelte";
  import SsoFlyover from "$lib/components/integrations/SsoFlyover.svelte";

  import { querystring } from "svelte-spa-router";

  import { getAuth, OAuthProvider, getRedirectResult, linkWithPopup, linkWithRedirect } from "firebase/auth";

  import { onDestroy } from "svelte";
  import { makeApiRequest } from "$lib/utils/api";
  import { sidebar } from "$lib/stores/sidebar.svelte";
  import SuccessfulInviteFlyover from "$lib/components/integrations/SuccessfulInviteFlyover.svelte";
  import { firebaseApp } from "$lib/utils/env";
  import { FirebaseError } from "firebase/app";
  import ConnectionPendingFlyover from "$lib/components/integrations/ConnectionPendingFlyover.svelte";
  import { derived } from "svelte/store";

  let selectedProvider = $state(ssoProviders.entra);
  let emailLength = $state(0);
  let isConnectingSso = $state<boolean>(false);

  async function handleEmailSubmit({ emails }: { emails: string[] }) {
    if (!selectedProvider) {
      console.error("No selected SSO provider");
      return;
    }
    emailLength = emails.length;
    let otherAdmins = {};
    if (selectedProvider.name === $selectedOrgStore?.sso?.provider) {
      otherAdmins = Object.fromEntries($selectedOrgStore?.sso.invitedAdmins.map((email) => [email, false]));
    }
    try {
      await makeApiRequest(`${$selectedOrgStore?.resourcePrefix}/`, {
        sso: {
          provider: selectedProvider.name,
          admins: { ...otherAdmins, ...Object.fromEntries(emails.map((email) => [email, true])) },
        },
      });
      // TODO: hard-coded entra
      push(`/onboarding/connect_sso?action=inviteSuccess&provider=entra`);
    } catch (error) {
      console.error("Error sending invites:", error);
    }
  }

  async function connectMicrosoftAdmin() {
    if (!selectedProvider) {
      console.error("No selected SSO provider");
      return;
    }
    console.log("Connecting Microsoft Admin");
    const auth = getAuth(await firebaseApp);
    const provider = new OAuthProvider("microsoft.com");
    provider.setCustomParameters({
      // Force re-consent.
      prompt: "consent",
      // Target specific email with login hint.
      login_hint: auth.currentUser!.email || "",
    });

    try {
      if (auth.config.authDomain?.endsWith(".firebaseapp.com")) {
        await linkWithPopup(auth.currentUser!, provider);
        // TODO: hard-coded entra
        push(`/onboarding/connect_sso?action=connectPending&provider=entra`);
      } else {
        linkWithRedirect(auth.currentUser!, provider);
      }
    } catch (error: any) {
      if (error instanceof FirebaseError) {
        if (error.code == "auth/credential-already-in-use") {
          // this happens if a user has already connected to the auth provider in firebase (via another org)
          // this usually happens in dev/testing when we keep using the same test account, but delete orgs and sso config
          console.log("Error code: ", error.code, "Skipping...");
          // TODO: hard-coded entra
          push(`/onboarding/connect_sso?action=connectPending&provider=entra`);
        }
        console.log("Error code:", error.code);
      }
    }
  }

  const unsubFromQueryString = derived([querystring, selectedOrgStore], (b) => b).subscribe(async ([qs, org]) => {
    const sp = new URLSearchParams(qs);
    if (sp.has("provider") && ssoProviders.hasOwnProperty(sp.get("provider"))) {
      selectedProvider = ssoProviders[sp.get("provider")];
    }
    switch (sp.get("action")) {
      case "connect":
        const auth = getAuth(await firebaseApp);
        getRedirectResult(auth)
          .catch((error) => {
            // Handle error.
            console.error("Error getting redirect result:", error);
          })
          .then((result) => {
            if (result) {
              console.log("Handled login redirect - pushing to action=connectPending");
              // TODO: hard-coded entra
              push(`/onboarding/connect_sso?action=connectPending&provider=entra`);
            }
          });
        sidebar.snippet = SsoConsent;
        break;
      case "invite":
        sidebar.snippet = InviteAdmin;
        break;
      case "connectPending":
        sidebar.snippet = ConnectionPending;
        if (org && !isConnectingSso) {
          try {
            isConnectingSso = true;
            // PUT on org_setup object
            await makeApiRequest(`${org?.resourcePrefix}/`, {
              sso: {
                provider: selectedProvider.name,
                admins: Object.fromEntries(org?.sso.invitedAdmins.map((email) => [email, false]) ?? []),
              },
            });
            // Now we want to redirect to the org proper object
            // TODO: hard-coded to Entra, need to update selectedProvider to support id
            push(`/onboarding/connect_sso?action=connectSuccess&provider=entra`);
          } finally {
            isConnectingSso = false;
          }
        }
        break;
      case "connectSuccess":
        sidebar.snippet = ConnectSuccess;
        break;
      case "inviteSuccess":
        sidebar.snippet = InviteSuccess;
        break;
      default:
        sidebar.snippet = null;
        break;
    }
  });

  onDestroy(() => {
    unsubFromQueryString();
  });

  $effect(() => {
    if ($selectedOrgStore?.id) {
      if (!$querystring) {
        // Sometimes, a new user's first login is via SSO
        // after clicking "Start Onboarding" on the previous page, the user is pushed to this page
        // the backend would also immediately create an org for them with SSO connected, but this takes a few seconds
        // we can redirect them to their new org URL once the backend has done its thing
        push(`${$selectedOrgStore.pagePrefix}/connect_sso`);
      } else {
        push(`${$selectedOrgStore.pagePrefix}/connect_sso?${$querystring}`);
      }
    }
  });
</script>

{#snippet SsoConsent()}
  <SsoFlyover
    provider={selectedProvider}
    onAdminInvite={() => {
      // TODO: hard-coded to Entra, need to update selectedProvider to support id
      push(`/onboarding/connect_sso?action=invite&provider=entra`);
    }}
    onAdminConnect={connectMicrosoftAdmin}
  />
{/snippet}

{#snippet InviteAdmin()}
  <InviteAdminForm connectionId={selectedProvider.name} onSubmit={handleEmailSubmit} />
{/snippet}

{#snippet ConnectSuccess()}
  <SuccessfulFlyover
    connectionId={selectedProvider.name}
    successActionButtonText={`Connect Data Sources`}
    successAction={() => push(`${$selectedOrgStore?.pagePrefix}/connect_data`)}
  />
{/snippet}

{#snippet InviteSuccess()}
  <SuccessfulInviteFlyover connectionId={selectedProvider.name} emailsLength={emailLength} />
{/snippet}

{#snippet ConnectionPending()}
  <ConnectionPendingFlyover connectionId={selectedProvider.name} />
{/snippet}

<OnboardingWizardShell currentStep={0} onNext={() => push(`${$selectedOrgStore?.pagePrefix}/connect_data`)}>
  <h2 class="text-3xl font-semibold text-offblack mb-3">Connect to your Single-Sign On (SSO) provider</h2>
  <p class="font-semibold text-offblack">You will need an admin from your organization to set up SSO access</p>
  <div class="relative inset-0 flex items-center my-3" aria-hidden="true">
    <div class="w-full border-t border-gray-300"></div>
  </div>
  <ConfigureConnectionGrid
    items={Object.entries(ssoProviders).map(([k, v]) => v)}
    connected={$selectedOrgStore?.sso.connected ? [$selectedOrgStore.sso.provider] : []}
    pending={$selectedOrgStore?.sso.provider ? [$selectedOrgStore?.sso.provider] : []}
    onConnect={(item) => {
      // TODO: hard-coding to entra atm, need to extend ConfigureConnectionGrid to support Item.id (not just name)
      push(`/onboarding/connect_sso?action=connect&provider=entra`);
    }}
  />
</OnboardingWizardShell>
